import { Bell, BookOpen, CreditCard, HelpCircle, LayoutGrid, Menu, Search, Server, Settings, ShieldCheck, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { fetchSites } from '../../actions/sites';
import { getLicenses } from '../../helpers/licenseApi';
import {
  clearLicenseNavCache,
  initialLicenseNavVisibility,
  writeLicenseNavCache,
} from '../../helpers/licenseNavCache';
import useAuthRedirect from '../../hooks/useAuthRedirect';
import { logout } from '../../reducers/authSlice';
import { userFullName, userInitials } from './adminUtils';

const navGroups = [
  {
    label: 'Workspace',
    items: [
      { label: 'Self-Hosted', href: '/self-hosted', icon: Server, requiresLicenses: true },
      { label: 'Sites', href: '/sites', icon: LayoutGrid, countKey: 'sites' },
      { label: 'Billing', href: '/billing', icon: CreditCard },
      { label: 'Tutorials', href: '/tutorials', icon: BookOpen },
    ],
  },
  {
    label: 'Account',
    items: [
      { label: 'Settings', href: '/settings', icon: Settings },
      { label: 'Help & support', href: '/support', icon: HelpCircle },
    ],
  },
  {
    // Staff-only. The whole group is omitted for everyone else — an empty
    // heading would advertise that a staff surface exists.
    label: 'Staff',
    items: [
      { label: 'Ops Console', href: '/ops', icon: ShieldCheck, requiresSuperAdmin: true },
    ],
  },
];

const routeCrumbs = {
  '/self-hosted': ['Workspace', 'Self-Hosted'],
  '/sites': ['Workspace', 'Sites'],
  '/billing': ['Workspace', 'Billing'],
  '/tutorials': ['Workspace', 'Tutorials'],
  '/settings': ['Account', 'Settings'],
  '/support': ['Account', 'Help'],
};

function Sidebar({ open, onClose, hasLicenses }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const sites = useSelector((state) => state.sites.data);
  const siteCount = Array.isArray(sites) ? sites.length : 0;
  // Read the role inline rather than importing isSuperAdmin from the console's
  // RequireStaff — that import would pull console code out of its lazy chunk
  // and into the customer bundle, undoing the isolation.
  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <>
      <button className={`admin-scrim ${open ? 'is-open' : ''}`} onClick={onClose} type="button" aria-label="Close navigation" />
      <aside className={`admin-sidebar ${open ? 'is-open' : ''}`}>
        <div className="admin-brand-row">
          <Link className="north-mark" to="/sites" onClick={onClose}>
            <img
              src="/nc-logo-blue.svg"
              alt="North Commerce"
              style={{ height: "28px", width: "auto", display: "block" }}
            />
          </Link>
          <span className="admin-env">Admin</span>
          <button className="icon-button mobile-close" type="button" onClick={onClose} aria-label="Close navigation">
            <X size={16} />
          </button>
        </div>

        <nav className="admin-nav">
          {navGroups.map((group) => {
            const items = group.items.filter((item) => {
              if (item.requiresSuperAdmin && !isSuperAdmin) return false;
              // false: this account has no licenses, so omit the item.
              // null: the check is still in flight — keep the slot (skeleton)
              // so Sites / Billing / Tutorials don't jump when it resolves.
              if (item.requiresLicenses && hasLicenses === false) return false;
              return true;
            });
            // A group whose every item is gated away renders nothing at all —
            // a bare "Staff" heading would leak that the surface exists.
            if (items.length === 0) return null;
            return (
              <div className="admin-nav-group" key={group.label}>
                <div className="admin-nav-heading">{group.label}</div>
                {items.map((item) => {
                  if (item.requiresLicenses && hasLicenses !== true) {
                    return (
                      <span
                        aria-hidden="true"
                        className="admin-nav-item admin-nav-item-skeleton"
                        key={item.href}
                      >
                        <span className="admin-nav-skeleton-icon" />
                        <span className="admin-nav-skeleton-label">{item.label}</span>
                      </span>
                    );
                  }
                  const Icon = item.icon;
                  const count = item.countKey === 'sites' ? siteCount : null;
                  return (
                    <NavLink
                      className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}
                      key={item.href}
                      onClick={onClose}
                      to={item.href}
                    >
                      <Icon size={16} />
                      <span>{item.label}</span>
                      {count !== null ? <span className="admin-nav-count">{count}</span> : null}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="admin-user-card">
          <div className="admin-avatar">{userInitials(user)}</div>
          <div className="admin-user-copy">
            <div className="admin-user-name">{userFullName(user)}</div>
            <div className="admin-user-plan">CHARTER - ACTIVE</div>
          </div>
          <button
            className="admin-user-menu"
            type="button"
            onClick={() => dispatch(logout())}
            title="Sign out"
          >
            Sign out
          </button>
        </div>
      </aside>
    </>
  );
}

function TopNav({ onMenu }) {
  const location = useLocation();
  const crumbs =
    routeCrumbs[location.pathname] ||
    (location.pathname.startsWith('/sites/') ? ['Workspace', 'Sites', 'Detail'] : ['Workspace']);

  return (
    <header className="admin-topnav">
      <button className="icon-button menu-button" type="button" onClick={onMenu} aria-label="Open navigation">
        <Menu size={18} />
      </button>
      <div className="admin-crumbs">
        {crumbs.map((crumb, index) => (
          <span className={index === crumbs.length - 1 ? 'current' : ''} key={crumb}>
            {index > 0 ? <span className="separator">/</span> : null}
            {crumb}
          </span>
        ))}
      </div>
      <div className="admin-search">
        <Search size={14} />
        <span>Search sites, orders...</span>
        <kbd>Cmd K</kbd>
      </div>
      <button className="icon-button" type="button" aria-label="Notifications">
        <Bell size={16} />
      </button>
    </header>
  );
}

export default function AdminShell() {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  // true: show Self-Hosted. false: hosted-only (or signed out), hide it.
  // null: a token is present but we don't know yet — the sidebar reserves
  // the slot. Seeded from the last answer for this token so a refresh
  // doesn't pop the item in after GET /licenses returns.
  const [hasLicenses, setHasLicenses] = useState(initialLicenseNavVisibility);
  const { isLoading, hasCheckedRehydrate } = useAuthRedirect('/sites', '/login');

  useEffect(() => {
    if (!hasCheckedRehydrate || isLoading) return;
    const token = localStorage.getItem('CloudForge-Token');
    if (token) dispatch(fetchSites(token));
  }, [dispatch, hasCheckedRehydrate, isLoading]);

  useEffect(() => {
    let token = null;
    try {
      token = localStorage.getItem('CloudForge-Token');
    } catch {
      token = null;
    }
    // Signed-out visitors never see the item. Wait until auth has settled so
    // a token written during this mount isn't missed.
    if (token || !hasCheckedRehydrate || isLoading) return undefined;
    setHasLicenses(false);
    clearLicenseNavCache();
    return undefined;
  }, [hasCheckedRehydrate, isLoading]);

  useEffect(() => {
    let token = null;
    try {
      token = localStorage.getItem('CloudForge-Token');
    } catch {
      token = null;
    }
    if (!token) return undefined;

    // One shot, in parallel with token revalidation. Re-running this when
    // rehydrate flips would cancel the in-flight check and put the wait back.
    let active = true;
    getLicenses()
      .then((data) => {
        if (!active) return;
        const next = Array.isArray(data) && data.length > 0;
        setHasLicenses(next);
        writeLicenseNavCache(next);
      })
      .catch(() => {
        if (!active) return;
        // A failed check must not reveal the item. If we already painted
        // from cache, keep that answer instead of yanking the row.
        setHasLicenses((current) => (current === null ? false : current));
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="admin-app">
      <Sidebar open={open} onClose={() => setOpen(false)} hasLicenses={hasLicenses} />
      <div className="admin-main">
        <TopNav onMenu={() => setOpen(true)} />
        <main className="admin-page">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
